import React, { useState } from "react";
import { Table, Tag, Button, Dropdown, Input, message } from "antd";
import {
  ArrowUpRight,
  Check,
  Eye,
  MoreHorizontal,
  Pencil,
  Sparkles,
  Trash2,
  UserRoundX,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import DeleteResumeModal from "../optimizeResume/modals/DeleteResumeModal";

const ResumeTable = ({
  resumes,
  setSelectedUserIds,
  selectedUserIds,
  setUnAssignStudentFlag,
  viewResumeApi,
  deleteResumeApi,
  resumeRenameApi,
  singleSkeletonLoading,
}) => {
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);
  const [resumeToDelete, setResumeToDelete] = useState(null);

  const [editingResumeId, setEditingResumeId] = useState(null);
  const [editedFileName, setEditedFileName] = useState("");

  const handleEditStart = (record) => {
    setEditingResumeId(record.resumeIdentifier);
    setEditedFileName(record.resumeFileName);
  };

  const handleEditCancel = () => {
    setEditingResumeId(null);
    setEditedFileName("");
  };

  const handleEditSave = (record) => {
    // console.log("Save:", {
    //   resumeIdentifier: record.resumeIdentifier,
    //   fileName: editedFileName,
    // });

    if (!editedFileName) {
      message.error("Please provide file name.");
      return;
    }

    resumeRenameApi(record.resumeIdentifier, editedFileName);

    // Call API here

    setEditingResumeId(null);
  };

  const handleRedirectEdit = (resumeIdentifier) => {
    navigate(`/editResume?resumeIdentifier=${resumeIdentifier}`);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "ACTIVE":
        return "text-green-700";
      case "DRAFT":
        return "text-yellow-700";
      case "COMPLETED":
        return "text-blue-700";
      default:
        return "text-gray-700";
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedUserIds,

    onChange: (selectedRowKeys) => {
      setSelectedUserIds(selectedRowKeys);
    },
  };

  const actionItems = [
    {
      key: "edit",
      label: (
        <span className="text-gray-700 font-medium py-1 text-[.8rem] flex gap-[8px] items-center">
          {" "}
          <Sparkles size={15} />
          Optimize resume
        </span>
      ),
    },
    {
      key: "delete",
      label: (
        <span className="text-gray-700 font-medium py-1 text-[.8rem] flex gap-[8px] items-center">
          {" "}
          <Trash2 size={15} />
          Delete resume
        </span>
      ),
    },
  ];

  const columns = [
    {
      title: (
        <span className="text-[#475467] font-medium text-[.8rem]">
          File Name
        </span>
      ),
      dataIndex: "firstName",
      key: "name",
      // width: 400, // Increased width for Name column
      render: (_, record) => (
        <div>
          {editingResumeId === record.resumeIdentifier ? (
            <div className="flex items-center gap-2">
              <Input
                value={editedFileName}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(
                    /[^a-zA-Z0-9_()]/g,
                    "",
                  );
                  setEditedFileName(sanitized);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onPressEnter={() => handleEditSave(record)}
                size="small"
                className="max-w-[250px]"
              />

              <button
                onClick={() => handleEditSave(record)}
                className="text-green-600 hover:text-green-700"
              >
                <Check size={16} />
              </button>

              <button
                onClick={handleEditCancel}
                className="text-red-600 hover:text-red-700"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="font-semibold text-gray-800 mb-0 text-[.8rem] break-all">
                {record?.resumeFileName}
              </p>

              <button
                onClick={() => handleEditStart(record)}
                className="text-[#667085] hover:text-[#DC6803] transition-colors"
              >
                <Pencil size={14} />
              </button>
            </div>
          )}
          <p className="text-[.75rem] text-[#475467] font-normal">
            {record?.resumeIdentifier || "N/A"}
          </p>
        </div>
      ),
    },

    {
      title: (
        <span className="text-[#475467] font-medium text-[.8rem]">
          Uploaded On
        </span>
      ),
      // dataIndex: "batchName",
      key: "date",
      align: "center",
      // width: 60,
      render: (record) => (
        <span className="text-[#475467] font-normal text-[.8rem]">
          {new Date(record?.uploadedDate).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      title: (
        <div className="text-center text-[#475467] font-medium text-[.8rem]">
          Status
        </div>
      ),
      // dataIndex: "creationDate",
      // key: "creationDate",
      align: "center",
      // width: 40,
      render: (record) => (
        <span
          className={`inline-flex items-center gap-[7px] rounded-md border-[1px] border-gray-300 px-2 py-[3px] text-[.8rem] font-medium ${getStatusClass(
            record?.resumeOptimizerState,
          )}`}
        >
          <span className="w-[.42rem] h-[.42rem] rounded-full bg-current"></span>
          {record?.resumeOptimizerState
            .toLowerCase()
            .replace("_", " ")
            .replace(/\b\w/g, (char) => char.toUpperCase())}
        </span>
      ),
    },
    {
      title: (
        <div className="text-center text-[#475467] font-medium text-[.8rem]">
          View resume
        </div>
      ),
      align: "center",
      render: (record) => (
        <span className="text-[#475467] font-normal text-[.78rem] flex justify-center">
          {record?.resumeOptimizerState !== "DRAFT" ? (
            <button
              onClick={() => viewResumeApi(record?.resumeIdentifier)}
              // className="cursor-pointer hover:text-amber-600 transition-all duration-200"
              className="btn-orange flex gap-1 items-center px-[.7rem] py-1"
            >
              View
              {/* <Eye size={16} className="" /> */}
            </button>
          ) : (
            <button className="btn-disabled text-gray-400 cursor-not-allowed flex gap-1 items-center px-[.7rem] py-1">
              View
              {/* <Eye size={16} className="" /> */}
            </button>
          )}
        </span>
      ),
    },
    {
      title: (
        <div className="text-center text-[#475467] font-medium text-[.8rem]">
          Actions
        </div>
      ),
      key: "action",
      align: "center",
      // width: 40,
      render: (_, record) => (
        <span className="flex items-center gap-2 justify-center">
          <Dropdown
            menu={{
              items: actionItems,
              onClick: ({ key }) => {
                if (key === "delete") {
                  setResumeToDelete(record);
                  setDeleteModal(true);
                }
                if (key === "edit") {
                  handleRedirectEdit(record?.resumeIdentifier);
                }
              },
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreHorizontal size={16} />}
              className="!text-gray-500 !p-0 !w-6 !h-6"
            />
          </Dropdown>
        </span>
      ),
    },
  ];

  return (
    <div className="rounded-2xl bg-white shadow-sm">
      <Table
        columns={columns}
        dataSource={resumes}
        // rowKey="batchName"
        rowKey="resumeIdentifier"
        pagination={false}
        // rowSelection={{
        //   type: "checkbox",
        //   ...rowSelection,
        // }}
        scroll={{ x: 1100 }}
      />

      {deleteModal && (
        <DeleteResumeModal
          deleteModal={deleteModal}
          setDeleteModal={setDeleteModal}
          resume={resumeToDelete}
          deleteResumeApi={deleteResumeApi}
        />
      )}
    </div>
  );
};

export default ResumeTable;
